import { DEFAULT_EMAIL_COLLECTION_POPUP, DEFAULT_POINT_EMAIL_COLLECTION_SETTINGS } from '../../../helpers/constants';
import { FeatureType, FormField, GetEmailCaptureResponse, MapPopUp, PointEmailCaptureCustomization, PopUpTypeOld, SettingStructure } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, isDefined } from '../../../helpers/utils';

import { StateController } from '../../../helpers/state/state-management/state-controller';
import { detectMobileViewport } from '../../../helpers/device';
import { escapeCsvValue } from '../../../helpers/files';
import featuresInfo from '../../../helpers/state/features-info';
import { getEmailCapturesForTopic } from '../../../helpers/api/email-capture-api';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-capture-emails-addon')
export class AdminCaptureEmailsAddon extends LitElement {
    amplitude_callsite: string = 'Capture Emails Add On';

    @query('#email-collection')
    emailCollectionElement?: HTMLInputElement;

    @query('#point-email-collection')
    pointEmailCollectionElement?: HTMLInputElement;

    @property({type: Object})
    pointFormObject!: PointEmailCaptureCustomization;

    @state()
    is_point_form_active: boolean = false;

    @state()
    pointFormSettings: SettingStructure[] = [];

    @state()
    destinationEmailSetting?: SettingStructure;

    @state()
    customFieldIdSetting?: SettingStructure;

    @state()
    emailsRecorded: GetEmailCaptureResponse[] = [];

    @state()
    selected_tab: string = 'Map Pop Up';

    @state()
    formFieldList: FormField[] = [];

    private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        await this.startup();
        if(isDefined(this.emailCollectionElement)) {
            this.emailCollectionElement.checked = topicData.emailCapturePopUp?.is_active ?? DEFAULT_EMAIL_COLLECTION_POPUP.is_active;
        }

        if(isDefined(this.pointEmailCollectionElement)) {
            this.pointEmailCollectionElement.checked = topicData.topic?.point_email_capture_customization?.is_active ?? DEFAULT_POINT_EMAIL_COLLECTION_SETTINGS.is_active;
        }
    }

    async startup() {
        this.is_point_form_active = topicData.topic?.point_email_capture_customization?.is_active ?? DEFAULT_POINT_EMAIL_COLLECTION_SETTINGS.is_active;

        this.emailsRecorded = await getEmailCapturesForTopic(
            this.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey
        );

        this.pointFormObject =
            topicData.topic?.point_email_capture_customization ?? DEFAULT_POINT_EMAIL_COLLECTION_SETTINGS;

        this.destinationEmailSetting =
             {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'destination_email',
                title: 'Custom Destination Email',
                description: 'This is the email address that the form submissions will be sent to if the destination type is "Static Email".',
                textCharLimit: 50,
                initialValueString: topicData.topic?.point_email_capture_customization?.destination_email ?? topicData.topic?.email ?? '',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            };

        this.customFieldIdSetting =
            {
                fieldType: 'string',
                inputType: 'dropdown',
                fieldName: 'custom_field_id',
                title: 'Custom Field to Get Email From',
                description: 'Select the custom field to get the email from if the destination type is "Custom Field Email".',
                initialValueString: topicData.topic?.add_point_customization?.custom_fields?.find((field) => 'Email' in field.field_type)?._id?.$oid ?? '',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                dropdownOptions: topicData.topic?.add_point_customization?.custom_fields
                    ?.filter((field) => 'Email' in field.field_type)
                    .map((field) => ({
                        label: field.customizations?.header ?? field._id?.$oid ?? 'Field',
                        value: field._id?.$oid ?? ''
                    })) ?? [],
            };



        this.pointFormSettings = [
            // {
            //     fieldType: 'boolean',
            //     inputType: 'toggle',
            //     fieldName: 'is_active',
            //     title: 'Collect Emails on Points',
            //     initialValueBoolean: topicData.topic?.point_email_capture_customization?.is_active ?? false,
            //     flippedLanguage: false,
            //     hasEntitlement: featuresInfo.emailCaptureEnabled,
            //     featureType: FeatureType.EmailCapture,
            //     subfields: ['cta_button_text', 'destination_email', 'success_message', 'email_field_boolean', 'name_field_boolean', 'phone_field_boolean', 'additional_info_field_boolean'],
            // },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'cta_button_text',
                title: 'Call to Action Button Text',
                description: 'This text will be displayed on the button that opens the email collection form.',
                textCharLimit: 50,
                initialValueString: topicData.topic?.point_email_capture_customization?.cta_button_text ?? 'Reach Out',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'success_message',
                title: 'Submission Success Message',
                description: 'This message will be displayed to the user after they submit the form.',
                textCharLimit: 300,
                initialValueString: topicData.topic?.point_email_capture_customization?.success_message ?? 'Thanks for your submission!',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
            },
            {

                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'email_field_boolean',
                title: 'Ask for Email',
                description: 'If enabled, the user will be asked to provide their email',
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.email_field.is_active ?? true,
                flippedLanguage: false,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: true,
                subfields: ['email_field_required', 'email_field_label', 'email_field_placeholder'],
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'email_field_label',
                title: 'Custom Email Label',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.email_field.label ?? 'Email',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'email_field_placeholder',
                title: 'Email Placeholder Text',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.email_field.placeholder ?? 'Enter your email',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'name_field_boolean',
                title: 'Ask for Name',
                description: 'If enabled, the user will be asked to provide their name',
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.name_field.is_active ?? false,
                flippedLanguage: false,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
                subfields: ['name_field_required', 'name_field_label', 'name_field_placeholder'],
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'name_field_required',
                title: 'Required?',
                isHidden: true,
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.name_field.is_required ?? false,
                flippedLanguage: false,
                isSubfield: true,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'name_field_label',
                title: 'Custom Name Label',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.name_field.label ?? 'Name',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'name_field_placeholder',
                title: 'Name Placeholder Text',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.name_field.placeholder ?? 'Enter your name',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'phone_field_boolean',
                title: 'Ask for Phone Number',
                description: 'If enabled, the user will be asked to provide their phone number',
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.phone_field.is_active ?? false,
                flippedLanguage: false,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
                subfields: ['phone_field_required', 'phone_field_label', 'phone_field_placeholder'],
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'phone_field_required',
                title: 'Required?',
                isHidden: true,
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.phone_field.is_required ?? false,
                flippedLanguage: false,
                isSubfield: true,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'phone_field_label',
                title: 'Custom Phone Label',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.phone_field.label ?? 'Phone Number',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'phone_field_placeholder',
                title: 'Phone Placeholder Text',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.phone_field.placeholder ?? 'Enter your phone number',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'additional_info_field_boolean',
                title: 'Ask for Additional Info or Message',
                description: 'If enabled, the user will be asked to provide additional information or a message',
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.additional_info_field.is_active ?? false,
                flippedLanguage: false,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
                subfields: ['additional_info_field_required', 'additional_info_field_label', 'additional_info_field_placeholder'],
            },
            {
                fieldType: 'boolean',
                inputType: 'toggle',
                fieldName: 'additional_info_field_required',
                title: 'Required?',
                isHidden: true,
                initialValueBoolean: topicData.topic?.point_email_capture_customization?.additional_info_field.is_required ?? false,
                flippedLanguage: false,
                isSubfield: true,
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'additional_info_field_label',
                title: 'Custom Additional Info Label',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.additional_info_field.label ?? 'Additional Info',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'inputShort',
                fieldName: 'additional_info_field_placeholder',
                title: 'Additional Info Placeholder Text',
                isHidden: true,
                textCharLimit: 50,
                isSubfield: true,
                initialValueString: topicData.topic?.point_email_capture_customization?.additional_info_field.placeholder ?? 'Enter additional info or a message',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
            },
            {
                fieldType: 'string',
                inputType: 'dropdown',
                fieldName: 'destination_type',
                title: 'Destination Type',
                description: 'Select the destination type for the email collection form.',
                initialValueString: topicData.topic?.point_email_capture_customization?.destination_type ?? 'static_email',
                hasEntitlement: featuresInfo.emailCaptureEnabled,
                featureType: FeatureType.EmailCapture,
                isHidden: false,
                dropdownOptions: [
                { value: 'static_email', label: 'Static Email' },
                { value: 'point_owner', label: 'Point Creator Email' },
                { value: 'custom_field_email', label: 'Custom Field Email' }
                ],
                widthPercentage: 100,
            },
        ];
    }

    private async preview() {
        if (topicData.mapComponent?.mapDrawer) {
            await topicData.mapComponent?.closeMapDrawer();
        };
        this.requestUpdate();
        await topicData.mapComponent?.setMapPopUp(MapPopUp.EmailCaptureDesktop);
    }

    async togglePointForm (e: Event) {
        this.is_point_form_active = (e.target as HTMLInputElement).checked;
        this.pointFormObject.is_active = this.is_point_form_active;
        const updated_topic = await topicData.fetchAndUpdateTopicData({ point_email_capture_customization: this.pointFormObject }, this.amplitude_callsite);
        if (isDefined(updated_topic.point_email_capture_customization)) {
            this.pointFormObject = updated_topic.point_email_capture_customization;
            this.is_point_form_active = updated_topic.point_email_capture_customization.is_active;
        }
    }

    openUpgradeDrawerEmailCollection() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.EmailCapture
                },
            })
        );
    }

    async closePreview() {
        await topicData.mapComponent?.closeMapDrawer();
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        void delay(400);
        // find title in settingsList as either fieldnametoggle or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find((setting) => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
            setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
        this.requestUpdate();
    }

    private async hasChange(e: CustomEvent) {
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        switch (fieldToUpdate) {
            case 'is_active':
                this.pointFormObject.is_active = newUpdatedValue;
                break;
            case 'cta_button_text':
                this.pointFormObject.cta_button_text = newUpdatedValue;
                break;
            case 'destination_email':
                this.pointFormObject.destination_email = newUpdatedValue;
                break;
            case 'destination_type':
                this.pointFormObject.destination_type = newUpdatedValue;
                if (newUpdatedValue === 'custom_field_email') {
                    this.pointFormObject.custom_field_id = topicData.topic?.add_point_customization?.custom_fields?.find((field) => 'Email' in field.field_type)?._id ?? undefined;
                }
                break;
            case 'custom_field_id': {
                const field_oid = { $oid: newUpdatedValue };
                this.pointFormObject.custom_field_id = field_oid;
                break;
            }
            case 'success_message':
                this.pointFormObject.success_message = newUpdatedValue;
                break;
            case 'name_field_boolean':
                this.pointFormObject.name_field.is_active = newUpdatedValue;
                break;
            case 'name_field_required':
                this.pointFormObject.name_field.is_required = newUpdatedValue;
                break;
            case 'name_field_label':
                this.pointFormObject.name_field.label = newUpdatedValue;
                break;
            case 'name_field_placeholder':
                this.pointFormObject.name_field.placeholder = newUpdatedValue;
                break;
            case 'email_field_boolean':
                // this.pointFormObject.email_field.is_active = newUpdatedValue; shameless keep required for now
                break;
            case 'email_field_required':
                // this.pointFormObject.email_field.is_required = newUpdatedValue;  shameless keep required for now
                break;
            case 'email_field_label':
                this.pointFormObject.email_field.label = newUpdatedValue;
                break;
            case 'email_field_placeholder':
                this.pointFormObject.email_field.placeholder = newUpdatedValue;
                break;
            case 'phone_field_boolean':
                this.pointFormObject.phone_field.is_active = newUpdatedValue;
                break;
            case 'phone_field_required':
                this.pointFormObject.phone_field.is_required = newUpdatedValue;
                break;
            case 'phone_field_label':
                this.pointFormObject.phone_field.label = newUpdatedValue;
                break;
            case 'phone_field_placeholder':
                this.pointFormObject.phone_field.placeholder = newUpdatedValue;
                break;
            case 'additional_info_field_boolean':
                this.pointFormObject.additional_info_field.is_active = newUpdatedValue;
                break;
            case 'additional_info_field_required':
                this.pointFormObject.additional_info_field.is_required = newUpdatedValue;
                break;
            case 'additional_info_field_label':
                this.pointFormObject.additional_info_field.label = newUpdatedValue;
                break;
            case 'additional_info_field_placeholder':
                this.pointFormObject.additional_info_field.placeholder = newUpdatedValue;
                break;
        }

        const updated_topic = await topicData.fetchAndUpdateTopicData({ point_email_capture_customization: this.pointFormObject }, this.amplitude_callsite);
        if (isDefined(updated_topic.point_email_capture_customization)) {
            this.pointFormObject = updated_topic.point_email_capture_customization;
        }
    }

    handleChangeTab(tab: string) {
        this.selected_tab = tab;
    }

    async initiateCSVDownload() {
        this.emailsRecorded = await getEmailCapturesForTopic(
            this.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey
        );

        let content = 'Email,Title,Description,CTA,Timestamp,Point,User Name,Additional Info\n';

        for (const record of this.emailsRecorded) {
            content += `${escapeCsvValue(record.email ?? '')},${escapeCsvValue(record.text.title ?? '')},${escapeCsvValue(record.text.description ?? '')},${escapeCsvValue(record.text.cta ?? '')},${record.timestamp},${record.point_id ?? ''},${escapeCsvValue(record.name ?? '')},${escapeCsvValue(record.additional_info ?? '')}\n`;
        }

        const filename = 'emails.csv';

        const blob = new Blob([content], {
            type: 'text/csv;charset=utf-8;',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    render() {
        return html`
            <div  id="${detectMobileViewport() ? '' : 'container'}">
                    ${this.selected_tab === 'Map Pop Up'
                        ? html`
                    <div class="tab__content">
                        <edit-pop-up-component
                            .pop_up_id="${topicData.emailCapturePopUp?._id?.$oid}"
                            .pop_up_type=${PopUpTypeOld.LeadGeneration}
                            .feature_type=${FeatureType.EmailCapture}
                            .hasEntitlement=${featuresInfo.emailCaptureEnabled}
                            @preview-pop-up=${this.preview}
                            @open-upgrade-dialog=${this.openUpgradeDrawerEmailCollection}
                            >
                        </edit-pop-up-component>
                    </div>
                    `
                    : html`
                        <div class="tab_content">
                            <h4 class="sub-title">Message This Place Form</h4>
                            <div class="setting-switch">
                                <label
                                    class="switch-label"
                                    for="email-collection"
                                >
                                    Point Form Off
                                </label>
                                <form-switch
                                    class="switch-icon"
                                    id="point-email-collection"
                                    name="point-email-collection"
                                    ?checked=${this.is_point_form_active}
                                    @change=${featuresInfo.emailCaptureEnabled ? this.togglePointForm : this.openUpgradeDrawerEmailCollection}
                                >
                                </form-switch>
                                <label
                                    class="switch-label"
                                    for="email-collection"
                                >
                                    Point Form On
                                </label>
                            </div>
                            <br>
                            ${this.pointFormSettings.map((setting) => {
                            return html `
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @open-upgrade-dialog=${this.openUpgradeDrawerEmailCollection}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.pointFormSettings)}
                                    >
                                </setting-component>`;})}
                            ${topicData.topic?.point_email_capture_customization?.destination_type === 'custom_field_email' && isDefined(this.customFieldIdSetting)
                                ? html`
                                    <setting-component
                                        style="width:${this.customFieldIdSetting.widthPercentage ?? 100}%"
                                        .setting_details=${this.customFieldIdSetting}
                                        .is_hidden=${this.customFieldIdSetting.isHidden ?? false}
                                        @open-upgrade-dialog=${this.openUpgradeDrawerEmailCollection}
                                        @on-change-value=${this.hasChange}
                                        @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.pointFormSettings)}
                                    >
                                    </setting-component>`
                                : ''
                            }
                            ${isDefined(this.destinationEmailSetting) && topicData.topic?.point_email_capture_customization?.destination_type === 'static_email'
                                ? html`
                                    <setting-component
                                        style="width:${this.destinationEmailSetting.widthPercentage ?? 100}%"
                                        .setting_details=${this.destinationEmailSetting}
                                        .is_hidden=${this.destinationEmailSetting.isHidden ?? false}
                                        @open-upgrade-dialog=${this.openUpgradeDrawerEmailCollection}
                                        @on-change-value=${this.hasChange}
                                        @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.pointFormSettings)}
                                    >
                                    </setting-component>`
                                : ''
                            }
                        </div>`}
                </div>
            <div class="addon__footer">

            <mwc-button
                class="addon__preview"
                @click="${this.initiateCSVDownload}"
                outlined
                ?disabled=${this.emailsRecorded.length === 0 || !featuresInfo.emailCaptureEnabled}
            >
                Download ${this.emailsRecorded.length} Emails
            </mwc-button>

            ${this.selected_tab === 'Map Pop Up' ? html`
                <mwc-button
                    class="addon__preview"
                    @click="${this.preview}"
                    outlined
                >
                    Preview Pop Up
                </mwc-button>
            ` : ''}
            </div>
            <error-toast id="errorToast"></error-toast>

		`;
    }

    static get styles() {
        return css`
            #container {
                flex-direction: column;
                max-height: calc(100vh - 162px);
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 20px;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .form-input {
                width: 100%;
                margin: 14px 0;
            }

            .gatedField {
                display: block;
                cursor: pointer;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .advanced-settings__tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
                margin-bottom: 1rem;
                font-size: 12px;
                margin-top: 1rem;
                font-size: 0.9rem;
                --mdc-icon-size: 12px;
            }

            .advanced-settings__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .advanced-settings__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .advanced-settings__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .advanced-settings__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .tab__content {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

		    .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .color-picker {
                height: 0;
                width: 0;
                opacity: 0;
                margin: 0px;
            }

            .color-selector-wrapper {
                display: flex;
                flex-direction: row;
                gap: 10px;
                justify-content: flex-start;
                margin-top: 10px;
            }

            .color-selector {
                width: 20%;
                display: flex;
                flex-direction: column;
                text-align: center;
                align-items: center;
                justify-content: space-between;
                border: 1px solid var(--theme-lighter-text-color);
                border-radius: 5px;
                padding: 5px;
                font-size: .75rem;
            }

            .color-wrapper {
                display: flex;
                align-items: center;
            }

            .color-preview {
                height: 20px;
                width: 20px;
                border-radius: 50%;
            }

            .email-collection--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
                color: var(--theme-lighter-text-color);
                width: 100%;
            }

            .email-collection {
                width: 100%;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: 20px;
            }

            .setting-switch {
                align-items: center;
                display: flex;
                gap: 8px;
                width: 100%;
                justify-content: center;
            }

            .addon__save {
                --mdc-theme-primary: var(--theme-border-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
            }

            .addon__save--changed {
                --mdc-theme-primary: var(--main-success-color);
            }

            .addon__preview {
                --mdc-theme-primary: var(--main-primary-color);
                --mdc-button-outline-color: var(--main-primary-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
                cursor: pointer;
            }

            .display-number {
                display: flex;
                align-items: center;
                gap: 14px;
            }

            .time-delayed {
                width: 60px;
            }

            .download-link {
                font-weight: 700;
                text-decoration: underline;
                cursor: pointer;
            }
        `;
    }
}
